import React, { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { cartSaga } from "./saga";
import * as selectors from "./selectors";
import { useAuth } from "../auth";
import Loader from "../../components/Loader";
import { notification } from "antd";

const CartContext = React.createContext({
  details: {},
  addToCart: () => {},
  updateCart: () => {},
  placeOrder: () => {},
  removeFromCart: () => {},
  refreshCartAndRedirect: () => {},
  loading: false,
});

export const useCart = () => {
  return useContext(CartContext);
};

const CartProvider = ({ children }) => {
  const auth = useAuth();
  const authenticated = auth.loggedInUser;
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: cartSaga });
  const dispatch = useDispatch();

  const cartItems = useSelector(selectors.selectCart);
  const loading = useSelector(selectors.selectLoading);
  const error = useSelector(selectors.selectError);

  useEffect(() => {
    if (error && error !== "") {
      notification.error({
        message: "Error in placing order",
        description: error,
      });
      setTimeout(() => {
        dispatch(actions.initializeState());
      }, 1500);
    }
  }, [error, dispatch]);

  const addToCart = (cartPackage, cb) => {
    dispatch(actions.addToCart({ data: cartPackage, onSuccess: cb }));
  };

  const updateCart = (data, cb) => {
    if (data.sampleCount === "") {
      data.sampleCount = "0";
    }
    const payload = {
      id: cartItems._id,
      data,
    };
    dispatch(actions.updateCart(payload));
    cb(); // this should be passed as a cb to saga
  };

  const removeFromCart = (item, cb) => {
    const payload = {
      id: cartItems._id,
      data: { removePackage: [item._id] },
    };
    dispatch(actions.updateCart(payload));
  };

  const placeOrder = (cb) => {
    const payload = {
      data: { cartId: cartItems._id },
      cb,
    };
    dispatch(actions.placeOrder(payload));
  };

  useEffect(() => {
    if (authenticated) {
      dispatch(actions.fetchCart());
    }
  }, [dispatch, authenticated]);

  const refreshCartAndRedirect = (cb) => {
    dispatch(actions.fetchCart({ cb }));
  };

  const values = {
    addToCart,
    updateCart,
    placeOrder,
    removeFromCart,
    loading,
    details: cartItems,
    refreshCartAndRedirect,
  };

  return (
    <CartContext.Provider value={values}>
      <>
        {loading ? <Loader /> : ""}
        {children}
      </>
    </CartContext.Provider>
  );
};

export default CartProvider;
